import { memo, useMemo } from 'react';

import { Ellipsis } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuProps, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { type Board } from '@air/types';

import { WorkspaceSidebarBoardsListItemButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemButton';
import { useWorkspaceSidebarBoardDropdownOptions } from '~/components/v2/hooks/useWorkspaceSidebarBoardDropdownOptions';

export type WorkspaceSidebarBoardsListItemDropdownProps = Pick<DropdownMenuProps, 'onOpenChange' | 'open'> & {
  board: Board;
  onRenamingStart: () => void;
};

export const WorkspaceSidebarBoardsListItemDropdown = memo(
  ({ board, onOpenChange, open, onRenamingStart }: WorkspaceSidebarBoardsListItemDropdownProps) => {
    const { workspaceSidebarBoardDropdownOptions } = useWorkspaceSidebarBoardDropdownOptions({
      board,
      open,
      onRename: onRenamingStart,
    });

    const shouldShowMenu = useMemo(
      () => workspaceSidebarBoardDropdownOptions.filter((option) => option.type !== 'separator').length > 0,
      [workspaceSidebarBoardDropdownOptions],
    );

    if (!shouldShowMenu) return null;

    return (
      <DropdownMenu
        onOpenChange={onOpenChange}
        open={open}
        trigger={
          <WorkspaceSidebarBoardsListItemButton data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_MENU_BUTTON">
            <Ellipsis className="size-4" />
          </WorkspaceSidebarBoardsListItemButton>
        }
      >
        {renderDropdownItems({ options: workspaceSidebarBoardDropdownOptions })}
      </DropdownMenu>
    );
  },
);

WorkspaceSidebarBoardsListItemDropdown.displayName = 'WorkspaceSidebarBoardsListItemDropdown';
